import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, SparklesIcon } from "@heroicons/react/outline";
import { WorkflowOutputSuggestion } from "./interface";
import BetaLabel from "./BetaLabel";
import OutputSuggestionsPanel from "./OutputSuggestionsPanel";

interface Props {
  token: string;
  outputId: string;
  outputName: string;
  suggestions: WorkflowOutputSuggestion[];
  readOnly?: boolean;
  isGenerating?: boolean;
  onSuggestionsChange: (suggestions: WorkflowOutputSuggestion[]) => void;
  onNavigate: (navigationKey: string) => void;
  onClose: () => void;
}

function DocumentSuggestionsSidebar(props: Props) {
  const {
    token,
    outputId,
    outputName,
    suggestions,
    readOnly,
    isGenerating,
    onSuggestionsChange,
    onNavigate,
    onClose,
  } = props;
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasSuggestions = suggestions.length > 0;

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 pt-4 pb-3 bg-white">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold font-gilroy text-gray-600 hover:text-gray-800 transition-colors"
            onClick={onClose}
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            {t("Back")}
          </button>
          <div className="w-fit inline-flex items-center gap-1 text-xs font-semibold font-gilroy portant-ai-gradient-text">
            {t("Portant AI")}
            <SparklesIcon className="w-3.5 h-3.5 text-[#1875FB]" />
            <BetaLabel inline className="ml-0" />
          </div>
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto scrollbar-hidden pr-0.5">
        <div className="pt-2 pb-6 shrink-0">
          <h2 className="text-2xl font-gilroy font-semibold text-black leading-tight break-words text-balance">
            {t("Review Suggested Edits")}
          </h2>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed break-words">
            {outputName}
          </p>
        </div>

        <div className="sticky top-0 z-10 bg-white pointer-events-none">
          <div className="h-px bg-gray-200" />
        </div>

        <div className="pt-4 pb-3">
          <OutputSuggestionsPanel
            token={token}
            outputId={outputId}
            suggestions={suggestions}
            readOnly={readOnly}
            isGenerating={isGenerating}
            scrollContainerRef={scrollContainerRef}
            onSuggestionsChange={onSuggestionsChange}
            onNavigate={onNavigate}
          />
        </div>
      </div>

      {!hasSuggestions && (
        <div className="shrink-0 pt-4 pb-3 flex items-center gap-3 border-t border-gray-200">
          <button
            type="button"
            className="w-full h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150"
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
        </div>
      )}
    </div>
  );
}

export default DocumentSuggestionsSidebar;
